/**
 * Phép tính khung nhìn của Bộ soi ảnh — phóng, kéo, giữ điểm dưới con trỏ.
 *
 * Gốc là `features/gallery/components/compare-zoom.ts` (CEO duyệt 28/09/2026, đã kiểm bằng số);
 * tệp đó nay uỷ thác về đây với mức của màn kho ảnh (400%, lề 8px). Bộ soi dùng 800% (spec
 * 2026-10-28, mục 4.2).
 *
 * Quy ước: `scale` là tỉ lệ so với kích thước `img` truyền vào (bộ soi truyền kích thước TỆP GỐC ⇒
 * 1 = 100% gốc). `pan` là điểm của ẢNH (phần trăm ngang / dọc) đang nằm giữa khung.
 */

export type Pan = { x: number; y: number };
export type Size = { w: number; h: number };
/** "fit" = vừa khung (thấy trọn ảnh); số = tỉ lệ cố định. */
export type Zoom = "fit" | number;

export const VIEWER_MAX = 8;
export const VIEWER_STEPS = [0.25, 0.5, 1, 2, 4, 8] as const;
/** Mỗi điểm `deltaY` của bánh xe đổi tỉ lệ theo hàm mũ: lăn đều tay thì phóng đều. */
export const WHEEL_K = 0.0015;
/** Chụm hai ngón trên bàn di chuột gửi `deltaY` nhỏ hơn nhiều — cần hệ số lớn hơn. */
export const PINCH_K = 0.01;
export const PAN_CENTER: Pan = { x: 0.5, y: 0.5 };

/** Nấc + / − phải hơn mức vừa khung ít nhất 10%, không thì bấm như không. */
const ABOVE_FIT = 1.1;
const EPS = 1e-6;
const clamp01 = (v: number) => Math.min(1, Math.max(0, v));

/**
 * Tỉ lệ để thấy TRỌN ảnh trong khung (đã trừ lề). Không phóng ảnh nhỏ lên quá 100%. Chưa đo được
 * ảnh hay khung thì trả 1: chưa biết thì không bịa.
 */
export function fitScale(img: Size, box: Size, margin = 0): number {
  const w = box.w - 2 * margin;
  const h = box.h - 2 * margin;
  if (img.w <= 0 || img.h <= 0 || w <= 0 || h <= 0) return 1;
  return Math.min(1, w / img.w, h / img.h);
}

/** Nấc kế tiếp khi bấm + / −; bấm − mà hết nấc hơn vừa khung thì về "fit". */
export function nextStep(
  cur: number,
  dir: 1 | -1,
  fit: number,
  opts: { max?: number; steps?: readonly number[] } = {},
): Zoom {
  const max = opts.max ?? VIEWER_MAX;
  const steps = opts.steps ?? VIEWER_STEPS;
  const ok = (n: number) => n > fit * ABOVE_FIT;
  if (dir > 0) return steps.find((n) => n > cur + EPS && ok(n)) ?? max;
  const below = [...steps].reverse().find((n) => n < cur - EPS);
  return below !== undefined && ok(below) ? below : "fit";
}

/** Tỉ lệ sau một lần lăn / chụm. Thu nhỏ tới vừa khung thì về "fit"; không quá `max`. */
export function scaleAfterWheel(cur: number, deltaY: number, fit: number, k = WHEEL_K, max = VIEWER_MAX): Zoom {
  const next = cur * Math.exp(-deltaY * k);
  if (next <= fit + EPS) return "fit";
  return Math.min(max, next);
}

/** Điểm soi mới sau khi đổi tỉ lệ quanh một điểm của khung: điểm dưới con trỏ đứng yên. */
export function panAroundPoint(
  prev: { scale: number; pan: Pan },
  next: number,
  pt: { x: number; y: number },
  box: Size,
  img: Size,
): Pan {
  if (img.w <= 0 || img.h <= 0 || prev.scale <= 0 || next <= 0) return prev.pan;
  const w0 = img.w * prev.scale;
  const h0 = img.h * prev.scale;
  const u = (pt.x - (box.w / 2 - prev.pan.x * w0)) / w0;
  const v = (pt.y - (box.h / 2 - prev.pan.y * h0)) / h0;
  const w1 = img.w * next;
  const h1 = img.h * next;
  return { x: clamp01(u + (box.w / 2 - pt.x) / w1), y: clamp01(v + (box.h / 2 - pt.y) / h1) };
}

/** Kéo (dx, dy) điểm màn trên tấm đang bày w×h ⇒ điểm soi mới, không trượt ra ngoài ảnh. */
export function panAfterDrag(pan: Pan, dx: number, dy: number, w: number, h: number): Pan {
  if (w <= 0 || h <= 0) return pan;
  return { x: clamp01(pan.x - dx / w), y: clamp01(pan.y - dy / h) };
}

/** Vị trí đặt ảnh (góc trên-trái và cỡ, px) để điểm `pan` nằm giữa khung. */
export function placeImage(pan: Pan, scale: number, box: Size, img: Size) {
  const width = img.w * scale;
  const height = img.h * scale;
  return { left: box.w / 2 - pan.x * width, top: box.h / 2 - pan.y * height, width, height };
}

/** Phần ảnh đang thấy trong khung, tính theo phần trăm ảnh (cho bản đồ nhỏ). */
export function visibleRect(pan: Pan, scale: number, box: Size, img: Size) {
  const p = placeImage(pan, scale, box, img);
  if (p.width <= 0 || p.height <= 0) return { x: 0, y: 0, w: 1, h: 1 };
  const x0 = clamp01(-p.left / p.width);
  const y0 = clamp01(-p.top / p.height);
  const x1 = clamp01((box.w - p.left) / p.width);
  const y1 = clamp01((box.h - p.top) / p.height);
  return { x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
}
